import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { space as styledSpace, width, height, maxWidth, maxHeight, borderRadius } from 'styled-system'
import { transparentize } from 'polished'
import { radius, space } from 'theme'
import Image from './Image'
import { Text } from './Typography'
import { Box, Flex, Grid } from './Layout'
import { transformSecondsToDuration } from '../../routes/Calendar/helpers'

const StyledBox = styled.div`
  display: block;
  ${width};
  ${height};
  ${maxWidth};
  ${maxHeight};
  ${styledSpace};
  ${borderRadius};
`

const StyledPollWrapper = styled(Box)`
  overflow-y: hidden;
  width: 100%;
  height: 100%;
  font-size: ${({ $isSmallView }) => ($isSmallView ? '10.666666px' : '16px')};
`

const StyledPollImage = styled(Image)`
  filter: drop-shadow(2px 2px 2px rgb(0 0 0 / 0.2));
`

const StyledPollOption = styled(Flex)`
  align-items: center;
  justify-content: center;
  display: flex;
  max-width: 100%;
  min-height: 2em;
  transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1), background-color 167ms cubic-bezier(0.4, 0, 0.2, 1),
    box-shadow 167ms cubic-bezier(0.4, 0, 0.2, 1), color 167ms cubic-bezier(0.4, 0, 0.2, 1);
  padding: 0 1em;
  border-radius: ${radius.pill};
  border: 1px solid ${({ theme }) => theme.colors.primary};
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => transparentize(0.9, theme.colors.primary)};
  }
`

const ICON = '/assets/poll.svg'

const Poll = ({
  source,
  showFallback,
  isSmallView,
  isMediumView,
  isLargeView,
  isResultsView,
  showPollIcon,
  showDuration,
  handleClickVoteOption,
}) => {
  const { options = [], title, duration } = source

  const collectDurationString = () => {
    const { days, hours, minutes } = transformSecondsToDuration({ duration })

    const duration_text = []

    if (days > 0) {
      duration_text.push(`${days} ${days === 1 ? 'day' : 'days'}`)
    }

    if (hours > 0) {
      duration_text.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`)
    }

    if (minutes > 0) {
      duration_text.push(`${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`)
    }

    return duration_text.join(', ')
  }

  let component = ''

  if (!showFallback) {
    if (isSmallView) {
      component = (
        <StyledPollWrapper $isSmallView={isSmallView} position="relative" p="xs">
          {showPollIcon && (
            <Box position="absolute" top={space.xs} right={space.xs}>
              <StyledPollImage src={ICON} alt={title} width="14px" height="14px" borderRadius={radius.l} />
            </Box>
          )}

          <Text whiteSpace="pre-line" fontSize="0.75em" textAlign="left">
            {title}
          </Text>
        </StyledPollWrapper>
      )
    } else if (isMediumView) {
      const optionsLength = options.length

      const duration_string = collectDurationString()

      component = (
        <StyledPollWrapper $isSmallView={isSmallView} position="relative" p="s">
          {showPollIcon && (
            <Box position="absolute" top={space.xs} right={space.xs}>
              <StyledPollImage src={ICON} alt={title} width="16px" height="16px" borderRadius={radius.l} />
            </Box>
          )}

          <Text whiteSpace="pre-line" fontSize="0.875em" textAlign="left">
            {title}
          </Text>
          <Text mt="xs" fontSize="0.75em" color="secondaryText" textAlign="left">
            {optionsLength} options · {duration_string}
          </Text>
        </StyledPollWrapper>
      )
    } else if (isLargeView) {
      const duration_string = showDuration && collectDurationString()

      component = (
        <StyledPollWrapper p="m">
          <Text whiteSpace="pre-line" fontSize="0.9375em" textAlign="left">
            {title}
          </Text>

          <Grid gridTemplateColumns="1fr" mt="1em" width="100%" gridGap="0.25em">
            {options.map(({ label, position }) => {
              return (
                <StyledPollOption
                  key={position}
                  onClick={() => {
                    handleClickVoteOption({ label, position })
                  }}
                >
                  <Text color="primary" fontWeight="bold" fontSize="0.875em" textAlign="center">
                    {label}
                  </Text>
                </StyledPollOption>
              )
            })}
          </Grid>

          {showDuration && (
            <Text alignItems="center" mt="0.75em">
              <Text as="span" fontSize="0.875em" color="secondaryText">
                {duration_string} left
              </Text>
            </Text>
          )}
        </StyledPollWrapper>
      )
    } else if (isResultsView) {
      component = (
        <StyledPollWrapper p="m">
          <Text whiteSpace="pre-line" fontSize="0.9375em" textAlign="left" color="primaryText">
            {title}
          </Text>

          <Grid gridTemplateColumns="1fr" mt="1em" width="100%" gridGap="0.25em">
            {options.map(({ label, position, percentage = 0 }) => {
              return (
                <Flex key={position} alignItems="center" minHeight="2em" width="100%" justifyContent="space-between">
                  <Flex width="calc(100% - 35px)" position="relative" height="100%" alignItems="center">
                    <Flex
                      bg="secondaryText"
                      width={percentage ? `${percentage}%` : '1px'}
                      height="100%"
                      borderRadius="0.25em"
                      position="absolute"
                      left="0"
                      zIndex={0}
                      opacity={0.2}
                    />
                    <Flex zIndex={1}>
                      <Text color="primaryText" fontSize="0.875em" px="0.3125em">
                        {label}
                      </Text>
                    </Flex>
                  </Flex>

                  <Flex width="35px" justifyContent="flex-end">
                    <Text color="primaryText" fontSize="0.875em">
                      {percentage}%
                    </Text>
                  </Flex>
                </Flex>
              )
            })}
          </Grid>
        </StyledPollWrapper>
      )
    }
  }

  return (
    <StyledBox borderRadius={radius.l} width="100%" height="100%">
      {showFallback ? (
        <Image src={ICON} alt={title} width="100%" height="100%" borderRadius={radius.l} />
      ) : (
        <Fragment>{component}</Fragment>
      )}
    </StyledBox>
  )
}

Poll.defaultProps = {
  source: {},
  showFallback: false,
  isSmallView: true,
  isMediumView: false,
  isLargeView: true,
  isResultsView: false,
  showPollIcon: true,
  showDuration: true,
  handleClickVoteOption: () => {},
}

Poll.propTypes = {
  source: PropTypes.object,
  showFallback: PropTypes.bool,
  isSmallView: PropTypes.bool,
  isMediumView: PropTypes.bool,
  isLargeView: PropTypes.bool,
  isResultsView: PropTypes.bool,
  showPollIcon: PropTypes.bool,
  showDuration: PropTypes.bool,

  handleClickVoteOption: PropTypes.func,
}

export default Poll
